import { useState } from 'react'
import {
    ContentAction,
    ContentActionView,
    ContentView,
    useGetUserIfAllowedQuery,
    useTakeContentActionMutation
} from '../../../api/client'
import { findAction, isActionApplied } from '../../content/content-card/actions'
import BaseActionButton from './BaseActionButton'
import { mapToContentActionView } from '../../../models/ContentAction'
import { toastError } from '../../Notification'

type Props = {
    content: ContentView
    action: ContentAction
    tooltip: string
    colour: string
    icon: JSX.Element
    onActionClick: (actions: ContentActionView[], queued?: boolean) => void
    actions: ContentActionView[]
    disabled?: boolean
    disabledMessage?: string
}

/**
 * A "generic" action is one which uses the takeAction mutation.
 *
 * Some actions, like "like" or "reply" are more complex and have dedicated
 * endpoints. Some like "hide", "mute", etc are "generic" and share the same
 * endpoint.
 *
 * This uses the BaseActionButton at its core.
 */
export default function GenericActionButton(props: Props) {
    const { content, action, tooltip, colour, icon, onActionClick, actions, disabled, disabledMessage } = props

    const contentAction = findAction(actions, action)

    const [takeContentActionMutation, { loading: loadingTakeContentAction }] =
        useTakeContentActionMutation({
            onError: (error) => {
                toastError(error.message)
            }
        })

    const { data: userData, loading: userLoading } = useGetUserIfAllowedQuery({
        variables: {
            id: contentAction?.userId || -1
        },
        skip: contentAction?.userId === undefined || contentAction.userId === null,

        onError: (error) => {
            console.error('There was an error fetching user details', error)
        }
    })

    const actionQueued = actions.some(
        (contentActionView) =>
            contentActionView.action.toLowerCase() === ContentAction.Hide.toLowerCase() &&
            contentActionView.queued
    )

    const [isActionQueued, setActionQueued] = useState(actionQueued)

    const shouldAlsoHide = function (
        action: ContentAction,
        alreadyApplied: boolean,
        alreadyHidden: boolean
    ) {
        if (
            // If a user is marking content as spam we should also hide it.
            action === ContentAction.ConfirmSpam &&
            !alreadyApplied &&
            !alreadyHidden
        ) {
            return true
        } else {
            return false
        }
    }

    let displayTooltip = tooltip

    if (isActionQueued) {
        displayTooltip = `${tooltip}. This action is queued.`
    }

    const actionAlreadyApplied = isActionApplied(actions, action)
    const hiddenActionAlreadyApplied = isActionApplied(actions, ContentAction.Hide)

    if (contentAction && contentAction?.active) {
        if (contentAction?.automated) {
            displayTooltip = `${displayTooltip}.<br /><br /> This action was taken automatically on ${new Date(
                contentAction.actionOn
            ).toUTCString()}`
        } else {
            displayTooltip = `${displayTooltip}. <br /><br />This action was taken by user ${
                userLoading ? '...loading...' : userData?.getUserIfAllowed.username
            } on ${contentAction.actionOn}`
        }
    } else if (contentAction && !contentAction.active) {
        // This was active but has been undone - the user needs to know this
        if (userData?.getUserIfAllowed.username) {
            displayTooltip = `${displayTooltip}. <br /><br />This action was previously applied but was undone by user ${userData.getUserIfAllowed.username}`
        }
    }

    return (
        <div className="relative">
            <BaseActionButton
                tooltip={displayTooltip}
                loading={loadingTakeContentAction}
                colour={colour}
                actionAlreadyApplied={actionAlreadyApplied}
                icon={icon}
                disabled={disabled}
                disabledMessage={disabledMessage}
                onActionClick={async () => {
                    // Set up additional action in case we want to take multiple actions
                    let additionalHideAction
                    let isHiddenQueued
                    if (shouldAlsoHide(action, actionAlreadyApplied, hiddenActionAlreadyApplied)) {
                        // Try to hide the comment if required
                        const hiddenResult = await takeContentActionMutation({
                            variables: {
                                contentId: content.id,
                                action: ContentAction.Hide,
                                toggle: !actionAlreadyApplied,
                                baseUrl: `${window.location.protocol}//${window.location.host}/`
                            }
                        })
                        if (!!!hiddenResult.errors) {
                            if (hiddenResult.data && hiddenResult.data.takeContentAction) {
                                isHiddenQueued = hiddenResult.data.takeContentAction.queued
                                additionalHideAction =
                                    hiddenResult.data?.takeContentAction?.contentAction
                            }
                        }
                    }

                    const result = await takeContentActionMutation({
                        variables: {
                            contentId: content.id,
                            action,
                            toggle: !actionAlreadyApplied,
                            baseUrl: `${window.location.protocol}//${window.location.host}/`
                        }
                    })

                    if (!!!result.errors) {
                        if (result.data && result.data.takeContentAction) {
                            const isQueued = result.data.takeContentAction.queued
                            const newAction = result.data?.takeContentAction?.contentAction

                            const queuedActions = []
                            const takenActions = []

                            if (newAction) {
                                const newActionMapped = mapToContentActionView(newAction)
                                const hiddenActionMapped = additionalHideAction
                                    ? mapToContentActionView(additionalHideAction)
                                    : null

                                if (isQueued) {
                                    queuedActions.push(newActionMapped)
                                } else {
                                    takenActions.push(newActionMapped)
                                }

                                if (isHiddenQueued && hiddenActionMapped) {
                                    queuedActions.push(hiddenActionMapped)
                                } else if (hiddenActionMapped) {
                                    takenActions.push(hiddenActionMapped)
                                }

                                if (queuedActions.length > 0) {
                                    setActionQueued(isQueued)
                                    onActionClick(
                                        queuedActions,
                                        result.data?.takeContentAction.queued
                                    )
                                } else {
                                    onActionClick(takenActions)
                                }
                            }
                        }
                    }
                }}
                isActionQueued={isActionQueued}
            />
        </div>
    )
}
